/**
 * MisCentrosPanel — Activity summary for ops users with assigned RLS sedes.
 * Shows each center's status (activo/atencion/inactivo) based on days since
 * last OPS, Liderazgo, or Near Miss activity.
 * Row click navigates to OPS or Liderazgo pre-filtered by that sede.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { MapPin, ChevronDown, ChevronUp, AlertTriangle, CheckCircle2, Clock, HelpCircle, ArrowRight } from 'lucide-react';
import { fetchMisCentrosResumen } from '@/services/api';
import { useUserStore } from '@/store/userStore';
import { useFiltersStore } from '@/store/filtersStore';

const STATUS_CONFIG = {
  activo:    { label: 'Activo',       color: '#00C853', bg: 'rgba(0,200,83,0.1)',    border: 'rgba(0,200,83,0.3)',   Icon: CheckCircle2  },
  atencion:  { label: 'Atención',     color: '#FFC107', bg: 'rgba(255,193,7,0.1)',   border: 'rgba(255,193,7,0.3)',  Icon: Clock         },
  inactivo:  { label: 'Inactivo',     color: '#FF5252', bg: 'rgba(255,82,82,0.1)',   border: 'rgba(255,82,82,0.3)',  Icon: AlertTriangle },
  sin_datos: { label: 'Sin datos',    color: '#9E9E9E', bg: 'rgba(158,158,158,0.08)', border: 'rgba(158,158,158,0.2)', Icon: HelpCircle  },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.sin_datos;
  const { Icon } = cfg;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
      style={{ color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}` }}
    >
      <Icon size={10} />
      {cfg.label}
    </span>
  );
}

function SummaryPill({ count, status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.sin_datos;
  if (!count) return null;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
      style={{ color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}` }}
    >
      {count} {cfg.label}{count !== 1 ? 's' : ''}
    </span>
  );
}

export function MisCentrosPanel() {
  const { user } = useUserStore();
  const navigate = useNavigate();
  const setFilter = useFiltersStore(s => s.setFilter);
  const [collapsed, setCollapsed] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [hoveredRow, setHoveredRow] = useState(null);

  // Only render for users with explicit sedes (ops users)
  const hasSedes = user?.sedes && user.sedes.length > 0;

  const { data, isLoading } = useQuery({
    queryKey: ['mis-centros-resumen'],
    queryFn: fetchMisCentrosResumen,
    staleTime: 5 * 60 * 1000,
    enabled: !!(user && hasSedes),
  });

  // Navigate to module with sede pre-filtered
  const goToModule = (path, sediCodigo, nombre) => {
    // Format matches backend split on " - " to extract code
    setFilter('sede', `${sediCodigo} - ${nombre}`);
    navigate(path);
  };

  if (!hasSedes || !data) return null;

  const centros = data.centros || [];
  const summary = data.summary || {};
  const visible = showAll ? centros : centros.slice(0, 10);
  const hasMore = centros.length > 10;

  return (
    <div
      data-testid="mis-centros-panel"
      className="rounded-xl border overflow-hidden"
      style={{ background: 'var(--vz-bg-elevated)', borderColor: 'var(--vz-border-subtle)' }}
    >
      {/* Header */}
      <button
        data-testid="mis-centros-toggle"
        className="w-full flex items-center justify-between px-4 py-3 hover:opacity-80 transition-opacity"
        onClick={() => setCollapsed(c => !c)}
      >
        <div className="flex items-center gap-2.5">
          <MapPin size={15} style={{ color: 'var(--vz-accent)' }} />
          <span className="font-montserrat text-sm font-semibold" style={{ color: 'var(--vz-text-primary)' }}>
            Mis Centros
          </span>
          <span className="text-[11px]" style={{ color: 'var(--vz-text-muted)' }}>
            {centros.length} sedes asignadas
          </span>
        </div>
        <div className="flex items-center gap-2">
          {!collapsed && (
            <div className="flex items-center gap-1.5 flex-wrap justify-end">
              <SummaryPill count={summary.inactivo}  status="inactivo" />
              <SummaryPill count={summary.sin_datos} status="sin_datos" />
              <SummaryPill count={summary.atencion}  status="atencion" />
              <SummaryPill count={summary.activo}    status="activo" />
            </div>
          )}
          {collapsed
            ? <ChevronDown size={16} style={{ color: 'var(--vz-text-muted)' }} />
            : <ChevronUp   size={16} style={{ color: 'var(--vz-text-muted)' }} />
          }
        </div>
      </button>

      {/* Body */}
      {!collapsed && (
        <div className="border-t" style={{ borderColor: 'var(--vz-border-subtle)' }}>
          {isLoading ? (
            <div className="px-4 py-6 text-center text-xs" style={{ color: 'var(--vz-text-muted)' }}>
              Cargando actividad de centros...
            </div>
          ) : centros.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs" style={{ color: 'var(--vz-text-muted)' }}>
              No hay centros asignados
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--vz-border-subtle)', background: 'var(--vz-bg-base)' }}>
                      {['Centro', 'OPS', 'Lid', 'NM', 'Última actividad', 'Días', 'Estado', ''].map(h => (
                        <th
                          key={h}
                          className="px-3 py-2 text-left font-semibold font-montserrat tracking-wide"
                          style={{ color: 'var(--vz-text-muted)', fontSize: '10px' }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((c, i) => (
                      <tr
                        key={c.sede_codigo}
                        data-testid={`mis-centros-row-${c.sede_codigo}`}
                        onMouseEnter={() => setHoveredRow(c.sede_codigo)}
                        onMouseLeave={() => setHoveredRow(null)}
                        style={{
                          borderBottom: i < visible.length - 1 ? '1px solid var(--vz-border-subtle)' : 'none',
                          background: hoveredRow === c.sede_codigo
                            ? 'var(--vz-bg-hover, rgba(255,255,255,0.04))'
                            : i % 2 === 0 ? 'transparent' : 'var(--vz-bg-base)',
                          transition: 'background 0.12s',
                        }}
                      >
                        {/* Name + code */}
                        <td className="px-3 py-2.5 max-w-[200px]">
                          <div className="font-medium truncate" style={{ color: 'var(--vz-text-primary)' }} title={c.nombre}>
                            {c.nombre}
                          </div>
                          <div style={{ color: 'var(--vz-text-muted)', fontSize: '10px' }}>{c.sede_codigo}</div>
                        </td>
                        {/* Counts */}
                        <td className="px-3 py-2.5 tabular-nums" style={{ color: c.ops_count > 0 ? '#4A9EFF' : 'var(--vz-text-muted)' }}>
                          {c.ops_count || '—'}
                        </td>
                        <td className="px-3 py-2.5 tabular-nums" style={{ color: c.lid_count > 0 ? '#00C853' : 'var(--vz-text-muted)' }}>
                          {c.lid_count || '—'}
                        </td>
                        <td className="px-3 py-2.5 tabular-nums" style={{ color: c.nm_count > 0 ? '#FF6B35' : 'var(--vz-text-muted)' }}>
                          {c.nm_count || '—'}
                        </td>
                        {/* Last activity date */}
                        <td className="px-3 py-2.5" style={{ color: 'var(--vz-text-secondary)', whiteSpace: 'nowrap' }}>
                          {c.last_activity || <span style={{ color: 'var(--vz-text-muted)' }}>—</span>}
                        </td>
                        {/* Days since */}
                        <td className="px-3 py-2.5 tabular-nums font-medium">
                          {c.dias_sin_actividad !== null && c.dias_sin_actividad !== undefined ? (
                            <span style={{
                              color: c.dias_sin_actividad < 30 ? '#00C853'
                                : c.dias_sin_actividad < 60 ? '#FFC107' : '#FF5252'
                            }}>
                              {c.dias_sin_actividad}d
                            </span>
                          ) : (
                            <span style={{ color: 'var(--vz-text-muted)' }}>—</span>
                          )}
                        </td>
                        {/* Status badge */}
                        <td className="px-3 py-2.5">
                          <StatusBadge status={c.status} />
                        </td>
                        {/* Quick-nav actions — appear on row hover */}
                        <td className="px-2 py-2.5 whitespace-nowrap">
                          <div
                            className="flex items-center gap-1"
                            style={{
                              opacity: hoveredRow === c.sede_codigo ? 1 : 0,
                              transition: 'opacity 0.15s',
                            }}
                          >
                            <button
                              data-testid={`goto-ops-${c.sede_codigo}`}
                              onClick={() => goToModule('/ops', c.sede_codigo, c.nombre)}
                              title={`Ver OPS de ${c.nombre}`}
                              className="flex items-center gap-0.5 px-1.5 py-0.5 rounded font-semibold transition-colors hover:opacity-80"
                              style={{
                                fontSize: '10px',
                                color: '#fff',
                                background: '#4A9EFF',
                              }}
                            >
                              OPS <ArrowRight size={9} />
                            </button>
                            <button
                              data-testid={`goto-lid-${c.sede_codigo}`}
                              onClick={() => goToModule('/liderazgo', c.sede_codigo, c.nombre)}
                              title={`Ver Liderazgo de ${c.nombre}`}
                              className="flex items-center gap-0.5 px-1.5 py-0.5 rounded font-semibold transition-colors hover:opacity-80"
                              style={{
                                fontSize: '10px',
                                color: '#fff',
                                background: '#00C853',
                              }}
                            >
                              Lid <ArrowRight size={9} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Show more / less */}
              {hasMore && (
                <div className="px-4 py-2.5 border-t flex items-center justify-center" style={{ borderColor: 'var(--vz-border-subtle)' }}>
                  <button
                    data-testid="mis-centros-show-more"
                    className="text-xs font-medium transition-opacity hover:opacity-70 flex items-center gap-1"
                    style={{ color: 'var(--vz-accent)' }}
                    onClick={() => setShowAll(s => !s)}
                  >
                    {showAll
                      ? <><ChevronUp size={12} /> Mostrar menos</>
                      : <><ChevronDown size={12} /> Ver {centros.length - 10} centros más</>
                    }
                  </button>
                </div>
              )}

              {/* Legend */}
              <div className="px-4 py-2 border-t flex gap-4 flex-wrap" style={{ borderColor: 'var(--vz-border-subtle)', background: 'var(--vz-bg-base)' }}>
                {[
                  { key: 'activo',    label: '< 30 días'    },
                  { key: 'atencion',  label: '30-60 días'   },
                  { key: 'inactivo',  label: '> 60 días'    },
                  { key: 'sin_datos', label: 'Sin registros'},
                ].map(({ key, label }) => {
                  const cfg = STATUS_CONFIG[key];
                  return (
                    <div key={key} className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full" style={{ background: cfg.color }} />
                      <span style={{ color: 'var(--vz-text-muted)', fontSize: '10px' }}>{cfg.label}</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
